import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Search, XCircle, CheckCircle2, CalendarClock } from "lucide-react";
import { euro } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_SHORT = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const MONTH_SHORT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addMinutes = (t, mins) => {
  const [h, m] = t.split(":").map(Number);
  const d = new Date(2000, 0, 1, h, m + mins);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const Reschedule = ({ booking, code, phone, onDone }) => {
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const days = useMemo(() => {
    const arr = [];
    const d = new Date();
    while (arr.length < 14) {
      if (d.getDay() !== 0) arr.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return arr;
  }, []);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    axios.get(`${API}/slots`, { params: { date, exclude: booking.id } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [date, booking.id]);

  const needed = booking.slots_count || 1;
  const available = (time) => {
    for (let i = 0; i < needed; i++) {
      const s = slots?.find((x) => x.time === addMinutes(time, 20 * i));
      if (!s || s.remaining <= 0) return false;
    }
    return true;
  };

  const move = async (time) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bookings/reschedule`, { code, phone, date, time });
      toast.success(`Appuntamento spostato al ${r.data.date} ore ${r.data.time}`);
      onDone(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile spostare");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reschedule-panel" className="lg:col-span-12 border-t border-[rgba(26,20,22,0.08)] pt-6">
      <p className="text-xs uppercase tracking-[0.25em] text-[#0B1F3A] font-mono-alt mb-4">Scegli il nuovo giorno</p>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {days.map((d) => {
          const iso = toISO(d);
          const active = iso === date;
          return (
            <button
              key={iso}
              data-testid={`reschedule-day-${iso}`}
              onClick={() => setDate(iso)}
              className={`shrink-0 rounded-xl border px-4 py-3 text-center transition-colors ${active ? "border-[#0B1F3A] bg-[#0B1F3A] text-[#F7F2EC]" : "border-[rgba(26,20,22,0.1)] bg-[#F7F2EC] hover:border-[#0B1F3A]"}`}
            >
              <p className="text-[10px] uppercase tracking-widest opacity-70">{DAY_SHORT[d.getDay()]}</p>
              <p className="font-display text-xl font-bold leading-none">{d.getDate()}</p>
              <p className="text-[10px] uppercase opacity-70">{MONTH_SHORT[d.getMonth()]}</p>
            </button>
          );
        })}
      </div>
      {date && (
        <div className="mt-4">
          {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#0B1F3A]" /> : (
            <div className="flex flex-wrap gap-2" data-testid="reschedule-slots">
              {(slots || []).map((s) => {
                const ok = available(s.time);
                return (
                  <button
                    key={s.time}
                    data-testid={`reschedule-slot-${s.time}`}
                    disabled={!ok || busy}
                    onClick={() => move(s.time)}
                    className={`rounded-lg border px-4 py-2 text-sm font-mono-alt transition-colors ${ok ? "border-[rgba(26,20,22,0.12)] bg-[#F7F2EC] hover:border-[#0B1F3A] hover:text-[#0B1F3A]" : "border-transparent bg-[#F7F2EC] opacity-30 line-through cursor-not-allowed"}`}
                  >
                    {s.time}
                  </button>
                );
              })}
              {slots && slots.length === 0 && <p className="text-sm text-[#6F6466]">Nessun orario disponibile.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ManageBooking = () => {
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState(null);
  const [moving, setMoving] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMoving(false);
    try {
      const r = await axios.get(`${API}/bookings/lookup`, { params: { code: code.trim(), phone: phone.trim() } });
      setFound(r.data);
    } catch (err) {
      setFound(null);
      toast.error(err.response?.data?.detail || "Prenotazione non trovata");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Confermi l'annullamento della prenotazione?")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bookings/cancel`, { code: code.trim(), phone: phone.trim() });
      setFound({ ...r.data, can_cancel: false });
      setMoving(false);
      toast.success("Prenotazione annullata. Il barbiere è stato avvisato.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile annullare");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="gestisci" data-testid="manage-booking-section" className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-20 lg:pb-32">
      <motion.div
        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.6 }}
        className="rounded-2xl border border-[rgba(11,31,58,0.2)] bg-[#FFFFFF] p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
      >
        <div className="lg:col-span-4">
          <p className="text-xs uppercase tracking-[0.25em] text-[#0B1F3A] font-mono-alt font-medium mb-3">Gestisci prenotazione</p>
          <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Sposta o annulla</h3>
          <p className="text-sm text-[#6F6466] mt-2">Codice + telefono. Gratis fino a 24 ore prima.</p>
        </div>

        <form onSubmit={lookup} className="lg:col-span-8 flex flex-col sm:flex-row gap-3">
          <input
            data-testid="manage-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Codice (es. 3F9A1B2C)"
            className="flex-1 rounded-xl border border-[rgba(26,20,22,0.1)] bg-[#F7F2EC] px-5 py-4 text-sm font-mono-alt outline-none focus:border-[#0B1F3A] transition-colors placeholder:text-[#9A8F90]"
          />
          <input
            data-testid="manage-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Telefono"
            className="flex-1 rounded-xl border border-[rgba(26,20,22,0.1)] bg-[#F7F2EC] px-5 py-4 text-sm outline-none focus:border-[#0B1F3A] transition-colors placeholder:text-[#9A8F90]"
          />
          <button
            type="submit"
            data-testid="manage-lookup-button"
            disabled={busy || code.trim().length < 4 || phone.trim().length < 6}
            className="rounded-xl bg-[#0B1F3A] text-[#F7F2EC] px-6 py-4 text-sm font-bold uppercase tracking-[0.12em] disabled:opacity-40 hover:bg-[#071528] transition-colors flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Cerca
          </button>
        </form>

        {found && (
          <div data-testid="manage-result" className="lg:col-span-12 border-t border-[rgba(26,20,22,0.08)] pt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-sm">
              <p className="font-display text-lg font-semibold">{found.name} · {found.date} ore <span className="text-[#0B1F3A]">{found.time}</span></p>
              <p className="text-[#6F6466]">{found.service_names.join(" + ")} · €{euro(found.total)}</p>
            </div>
            {found.status === "cancelled" ? (
              <span data-testid="manage-status-cancelled" className="inline-flex items-center gap-2 text-sm text-[#6F6466]"><XCircle className="w-4 h-4 text-[#0B1F3A]" /> Annullata</span>
            ) : found.can_cancel ? (
              <div className="flex flex-wrap gap-3">
                <button
                  data-testid="manage-reschedule-button"
                  onClick={() => setMoving((m) => !m)}
                  className="rounded-full bg-[#0B1F3A] text-[#F7F2EC] px-6 py-3 text-sm uppercase tracking-[0.12em] hover:bg-[#071528] transition-colors inline-flex items-center gap-2"
                >
                  <CalendarClock className="w-4 h-4" /> {moving ? "Chiudi" : "Sposta orario"}
                </button>
                <button
                  data-testid="manage-cancel-button"
                  onClick={cancel}
                  disabled={busy}
                  className="rounded-full border border-[#0B1F3A] text-[#0B1F3A] px-6 py-3 text-sm uppercase tracking-[0.12em] hover:bg-[#0B1F3A] hover:text-[#F7F2EC] transition-colors disabled:opacity-40"
                >
                  Annulla
                </button>
              </div>
            ) : (
              <span data-testid="manage-status-locked" className="inline-flex items-center gap-2 text-sm text-[#6F6466]"><CheckCircle2 className="w-4 h-4 text-[#0B1F3A]" /> Confermata · a meno di 24h chiama il negozio</span>
            )}
          </div>
        )}

        {found && moving && found.can_cancel && (
          <Reschedule booking={found} code={code.trim()} phone={phone.trim()} onDone={(b) => { setFound({ ...b, can_cancel: true }); setMoving(false); }} />
        )}
      </motion.div>
    </section>
  );
};

export default ManageBooking;
